import { TamediaStyles } from '@ta-interaktiv/microfrontend-template-helpers'
import styled, { keyframes } from 'styled-components'
import { useCallback, useEffect, useState, createRef } from 'react'
import { ContentWidth } from '@ta-interaktiv/react-disco-grid'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { GlobalVariables } from 'Globals/GlobalVariables'
import GridItem from 'components/GridItem'
import { useEffectOnce } from 'usehooks-ts'
import { nest } from 'd3-collection'
import { Waypoint } from 'react-waypoint'
import { cp } from 'fs'
import iconsVariables from 'Globals/icons.module.css'
import { RiCloseCircleFill } from 'react-icons/ri'
import { initializeApp } from 'firebase/app'

const firebaseConfig = {
  apiKey: 'AIzaSyDK2K4LgTvb2KcTE6bUMDElqGxYKCLlo-c',
  authDomain: 'literatur-tipps.europe-west1.firebasedatabase.app/',
  databaseURL: 'https://literatur-tipps.europe-west1.firebasedatabase.app/',
  storageBucket: 'ta-interaktiv.appspot.com',
  projectId: 'ta-interaktiv',
}

initializeApp(firebaseConfig)

const MainComponent = ({
  tenant,
  colorMode,
  properties,
  trackEvent,
}: MicroFrontendProps) => {
  const [rawData, setRawData] = useState<any[]>([])
  const [data, setData] = useState<any[]>([])
  const [maxMonths, setMaxMonths] = useState(0)
  const [filters, setFilters] = useState(new Map())
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const topRef = createRef<HTMLDivElement>()
  const [headerHeight, setHeaderHeight] = useState(0)

  // Gather all the Genre from the data
  const genreOptions = rawData
    .reduce((accumulator: any, currentValue: any) => {
      if (!accumulator.find((item: any) => item.value === currentValue.Genre)) {
        return [
          {
            value: currentValue.Genre,
            label: currentValue.Genre,
            count: 1,
          },
          ...accumulator,
        ]
      } else {
        accumulator.find(
          (item: any) => item.value === currentValue.Genre
        ).count += 1
        return accumulator
      }
    }, [])
    .map((item: any) => ({
      value: item.value,
      label: item.label + ' (' + item.count + ')',
    }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label))

  useEffectOnce(() => {
    fetch(
      'https://storage.googleapis.com/ta-interaktiv/literatur-tipps/tipps.json'
    )
      .then((response) => response.json())
      .then((rawData: any) => {
        const groupedData = nest()
          .key(function (d: any) {
            return d['Jahr-Monat']
          })
          .entries(rawData.data)
        setData(groupedData)
        setRawData(rawData.data)
      })
  })
  // console.log('genreOptions:', genreOptions)
  // console.log(data)

  useEffect(() => {
    // sticky header?
    const headerElement = document.querySelector('[class*=FullHeader_root]')

    let headerHeight = 0
    if (headerElement) {
      // check if header is sticky
      const headerStyle = window.getComputedStyle(headerElement)
      const isSticky =
        headerStyle.position === 'sticky' && headerStyle.top !== 'auto'

      if (isSticky) {
        headerHeight = headerElement.clientHeight
        setHeaderHeight(headerHeight)
      }
    }
  })

  const handleDropdownChange = (type: string, value: string) => {
    const newFilters = filters
    newFilters.has(type)
      ? newFilters.get(type).has(value)
        ? filters.get('type').size > 1
          ? newFilters.get(type).delete(value)
          : newFilters.clear()
        : newFilters.set(type, new Set([...newFilters.get(type), value]))
      : newFilters.set(type, new Set([value]))

    setFilters(new Map(newFilters))
    //console.log('filters', filters)

    const flattenFilters = new Set(
      [...filters.values()].reduce((a, b) => [...b, ...a], [])
    )

    const filteredItems =
      flattenFilters.size > 0
        ? rawData.filter((item: any) => {
            // console.log('filtering')
            // The data used to filter the items
            const itemFilterData: { [key: string]: any } = {
              type: item.Genre,
            }
            // For each filter, check if the item's data matches the filter
            return [...filters.keys()].every((filterType: string) => {
              // Get the filters for this key
              const filterItems = filters.get(filterType)
              // If this filter is empty, we don't care about it
              if (filterItems.size === 0) return true
              // Check if this filter contains the item's value for this key
              let isInFilters = filterItems.has(itemFilterData[filterType])
              return isInFilters
            })
          })
        : rawData
    setFilteredItems(filteredItems)
    // console.log('filteredItems', filteredItems)
  }

  const resetFilters = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' })
    setFilters(new Map())
  }

  return (
    <AppContainer $colorMode={colorMode} className={iconsVariables.globalIcons}>
      <ContentWidth>
        <FilterWrapper ref={topRef} style={{ paddingTop: headerHeight }}>
          <select
            id='genres-filter'
            value={'genre'}
            onChange={(event: any) => {
              // console.log('filter', event.target.value)
              handleDropdownChange('type', event.target.value)
            }}
          >
            <option key={'all'} value={'all'}>
              Genre filtern
            </option>
            {genreOptions.map((item: any) => (
              <option key={`option-${item.value}`} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          {filters.size > 0 && (
            <div>
              <div className='user-selection'>
                {[...filters.values()].map(function (item: Set<string>) {
                  return Array.from(item).map((filter) => (
                    <div
                      className='filter-label'
                      onClick={() => handleDropdownChange('type', filter)}
                      key={filter}
                    >
                      {filter}{' '}
                      <span>
                        <RiCloseCircleFill />
                      </span>
                    </div>
                  ))
                })}
              </div>
            </div>
          )}
        </FilterWrapper>

        {filters.size > 0 && (
          <div className='status' style={{ top: headerHeight - 1 }}>
            <div className='count'>
              {filteredItems.length} von {rawData.length} Büchern
            </div>
            <div className='reset'>
              <div className='btn' onClick={() => resetFilters()}>
                zurücksetzen
              </div>
            </div>
          </div>
        )}

        <RatingWrapper>
          {
            //map through data and create a GridItem for each item
            data &&
              filters.size === 0 &&
              data.map(function (month: any, index: number) {
                const monthSplit = month.key.split('-')
                const monthTitle = monthSplit[1]
                const monthYear = monthSplit[0]
                //map over each month limited by maxMonths
                if (index <= maxMonths) {
                  const items = month.values.map(
                    (item: any, itemIndex: number) => (
                      <GridItem
                        title={item.Titel}
                        short={item.Kurzbeschrieb}
                        authorrating={item.Rating}
                        genre={item.Genre}
                        image={item.Bild}
                        caption={item.Bildquelle}
                        text={item.Text}
                        key={itemIndex}
                        colorMode={colorMode}
                      ></GridItem>
                    ),
                    []
                  )

                  return (
                    <MonthWrapper>
                      {index > 0 && (
                        <div className='title-wrapper'>
                          <h2>{monthTitle}</h2>
                          <div className='year'>{monthYear}</div>
                        </div>
                      )}

                      <ResponsiveMasonry
                        columnsCountBreakPoints={{ 0: 1, 600: 2 }}
                      >
                        <Masonry gutter='5.4rem'>{items}</Masonry>
                      </ResponsiveMasonry>
                    </MonthWrapper>
                  )
                }
              })
          }

          {data && filters.size > 0 && (
            <ResponsiveMasonry columnsCountBreakPoints={{ 0: 1, 600: 2 }}>
              <Masonry gutter='5.4rem'>
                {filteredItems.map(
                  (item: any, itemIndex: number) => (
                    <GridItem
                      title={item.Titel}
                      short={item.Kurzbeschrieb}
                      authorrating={item.Rating}
                      genre={item.Genre}
                      image={item.Bild}
                      caption={item.Bildquelle}
                      text={item.Text}
                      key={itemIndex}
                      colorMode={colorMode}
                    ></GridItem>
                  ),
                  []
                )}
              </Masonry>
            </ResponsiveMasonry>
          )}

          <Waypoint
            scrollableAncestor={window}
            onEnter={(e) => {
              if (maxMonths < data.length) {
                // console.log('loading month ', maxMonths + 1, 'of', data.length)
                setMaxMonths(maxMonths + 1)
              }
            }}
            onLeave={(e) => {
              //console.log('leave ')
            }}
          >
            {maxMonths < data.length && filters.size === 0 && (
              <div className='loading-hint'>
                <span>Nächsten Monat laden</span>
                <div className='dot-flashing'></div>
              </div>
            )}
          </Waypoint>
        </RatingWrapper>
      </ContentWidth>
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)`
  ${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  margin-bottom: 4rem;
  // fix for shaking while scrolling in safari
  transform: translateZ(0);
  backface-visibility: hidden;

  .status {
    display: grid;
    grid-template-columns: 1fr 1fr;
    text-align: center;
    font-size: 1.3rem;
    line-height: 1.4rem;
    padding: 1.2rem 0;
    z-index: 99;
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    width: 100%;
    background-color: var(--site-background);
    .count {
      text-align: right;
      padding: 0.25rem 1rem;
      border-right: 1px solid var(--text-color);
    }
    .reset {
      text-align: left;
      padding: 0 1rem;
      .btn {
        text-align: center;
        color: var(--brandblue-main);
        font-size: 1.25rem;
        padding-right: 0.2rem;
        padding: 0.25rem 1rem 0.4rem;
        background-color: var(--beige-bright-2);
        border-radius: 0.11111111rem;
        display: inline-block;
        cursor: pointer;
      }
      .btn:hover {
        background-color: var(--brandblue-main);
        color: var(--gray-6);
      }
    }
  }
`
const MonthWrapper = styled.div`
  .title-wrapper {
    margin: 7em 0 2.4rem;
    h2 {
      margin: 4rem 0 1rem;
      text-align: center;
      font-size: 3.6rem;
      font-family: var(--font-serif);
    }
    .year {
      text-align: center;
      margin-bottom: 3rem;
    }
  }

  .title-wrapper:after,
  .title-wrapper:before {
    content: '';
    display: block;
    width: 6rem;
    border-bottom: 2px solid #ededed;
    text-align: center;
    margin: 2.4rem auto;
  }
`

const dotflashing = keyframes`
  0% {
    background-color: var(--loading-dot);
  }
  50%, 100% {
    background-color: var(--loading-higlight);
  }
`

const FilterWrapper = styled.div`
  margin: 0 auto;
  padding: 2rem 0 0;
  @media only screen and (max-width: 1111px) {
    padding: 2rem 1rem 0;
  }

  //dropdown
  select {
    /* Reset */
    appearance: none;
    border: 0;
    outline: 0;
    /* Personalize */
    width: 100%;
    max-width: 340px;
    height: 3.2em;
    padding: 0 4em 0 1em;
    background: var(--caret-down) no-repeat right 0.8em center / 1.4em,
      linear-gradient(
        to left,
        var(--zebra-color) 3em,
        var(--site-background) 3em
      );
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-color);
    border: 1px solid var(--border-color);
    cursor: pointer;
    /* Remove focus outline */
    &:focus {
      outline: none;
    }
    /* Remove IE arrow */
    &::-ms-expand {
      display: none;
    }
  }

  .user-selection {
    margin-top: 4rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #ededed;
    .filter-label {
      background-color: #e8e8e8;
      color: var(--brandblue-main);
      display: inline;
      font-size: 1.25rem;
      font-weight: 700;
      letter-spacing: 0.02rem;
      padding: 0.45rem 1.2rem 0.6rem;
      margin-right: 0.6rem;
      border-radius: 0.25rem;
      cursor: pointer;

      span {
        font-size: 1.6rem;
        top: 0.3rem;
        left: 0.2rem;
        position: relative;
      }
    }
    .filter-label:hover {
      background-color: var(--brandblue-main);
      color: var(--gray-6);
    }
  }
`

const RatingWrapper = styled.div`
  margin: 0 auto;
  @media only screen and (max-width: 1111px) {
    padding: 0 1.5rem;
  }

  .loading-hint {
    margin: 6rem auto 4rem;
    width: 100%;
    font-size: 1.6rem;
    text-align: center;
    font-style: italic;
    background-color: var(--loading-background);
    color: var(--loading-text);
    padding: 2rem;
    max-width: 340px;
  }

  .dot-flashing {
    position: relative;
    width: 7px;
    height: 7px;
    margin: 0.5rem auto 0.5rem;
    border-radius: 5px;
    background-color: var(--loading-dot);
    color: var(--loading-dot);
    animation: ${dotflashing} 1s infinite linear alternate;
    animation-delay: 0.5s;
  }
  .dot-flashing::before,
  .dot-flashing::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;
  }
  .dot-flashing::before {
    left: -15px;
    width: 7px;
    height: 7px;
    border-radius: 5px;
    background-color: var(--loading-dot);
    color: var(--loading-dot);
    animation: ${dotflashing} 1s infinite alternate;
    animation-delay: 0s;
  }
  .dot-flashing::after {
    left: 15px;
    width: 7px;
    height: 7px;
    border-radius: 5px;
    background-color: var(--loading-dot);
    color: var(--loading-dot);
    animation: ${dotflashing} 1s infinite alternate;
    animation-delay: 1s;
  }
`

export default MainComponent
